<script setup lang="ts">
import type { CustomerReferenceTag } from '~/types'
import IconAlternative from '~icons/mdi/shopping-search-outline'
import type { AlternativesEntry } from '~/composables/useAlternativesEntry'
import { useAlternativesButton } from '~/composables/useAlternativesButton'

interface Props {
  orderCondition?: { min?: number, max?: number, multiplier?: number }
  customerReference: Array<CustomerReferenceTag>
  lastOrderedQuantity?: number
  lastOrderedDate?: string | null
  alternatives: AlternativesEntry | null
  humanMedicineRestricted?: boolean
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'explore:alternatives'): void
}>()

const display = useDisplay()
const { feature } = useCountryConfig()
const localePath = useLocalePath()
const { hasCustomerReference } = useCustomerReferences()
const { isVisible: showAlternativesBtn, navTo: alternativesNavTo, handleClick: handleAlternativesClick } = useAlternativesButton(
  () => props.alternatives,
  () => emit('explore:alternatives'),
)

const quantityValue = defineModel<number>('quantityValue', { default: 0 })
const selectedReferenceName = defineModel<CustomerReferenceTag | undefined>('selectedReferenceName')

watch(() => props.customerReference ?? [], (customerRefs: Array<CustomerReferenceTag>) => {
  if (!customerRefs.length)
    return

  if (!selectedReferenceName.value || !customerRefs.includes(selectedReferenceName.value)) {
    selectedReferenceName.value = customerRefs[0]
  }
}, { immediate: true })
</script>

<template>
  <v-container fluid class="pa-0">
    <v-row align="center" no-gutters>
      <v-col
        cols="12"
        md="5"
        class="mb-2 mb-md-0"
      >
        <v-btn
          v-if="!props.humanMedicineRestricted && !hasCustomerReference(customerReference)"
          component="NuxtLink"
          data-nav-id="product_ref_add_reference"
          variant="tonal"
          class="text-vm-grey-8"
          :text="$t('searchResult.customerReference.title')"
          :to="localePath('/account/references')"
        />

        <v-btn
          v-if="!props.humanMedicineRestricted && !hasCustomerReference(customerReference) && showAlternativesBtn"
          :component="alternativesNavTo ? 'NuxtLink' : undefined"
          data-nav-id="product_ref_alternatives"
          variant="outlined"
          class="text-vm-grey-9 rounded-lg mr-auto mt-4 bg-vm-white"
          :append-icon="IconAlternative"
          :to="alternativesNavTo"
          @click="handleAlternativesClick"
        >
          {{ $t('product.alternative.button') }}
        </v-btn>

        <template v-if="!props.humanMedicineRestricted && feature.displayPharmacyReference">
          <vmz-btn-toggle-primary
            v-if="customerReference && customerReference.length === 1"
            v-model="selectedReferenceName"
            data-test-id="product-reference-button"
            data-track-id="product_reference_toggle_single"
            color="primary"
            class="font-weight-bold rounded-lg mr-auto"
            readonly
          >
            <vmz-btn
              component="NuxtLink"
              data-nav-id="product_ref_add_reference_icon"
              class="px-3"
              style="min-width: 5px"
              :to="localePath('/account/references')"
            >
              +
            </vmz-btn>
            <vmz-btn
              :value="customerReference[0]"
              data-track-id="product_reference_display"
              readonly
            >
              {{ $t(`customerReference.options.${customerReference[0]}`) }}
            </vmz-btn>
          </vmz-btn-toggle-primary>
          <vmz-btn-toggle-primary
            v-else
            v-model="selectedReferenceName"
            data-test-id="product-reference-button"
            data-track-id="product_reference_toggle"
            color="primary"
            class="mr-auto"
            mandatory
          >
            <vmz-btn
              v-for="referenceName in customerReference"
              :key="referenceName"
              class="px-3"
              data-track-id="product_select_reference"
              :value="referenceName"
            >
              {{ $t(`customerReference.options.${referenceName}`) }}
            </vmz-btn>
          </vmz-btn-toggle-primary>
        </template>
      </v-col>
      <v-col cols="12" md="7" class="d-flex flex-column flex-md-row align-end align-md-center">
        <v-container fluid class="pa-0">
          <v-row v-if="quantityValue < 1" no-gutters>
            <v-col :cols="lastOrderedQuantity && lastOrderedQuantity > 0 ? '10' : '12'" class="d-flex justify-end pr-2">
              <vmz-btn
                :disabled="props.humanMedicineRestricted || !hasCustomerReference(customerReference)"
                color="primary"
                class="font-weight-bold rounded-lg vmz-btn-add-to-cart w-100 w-sm-100 w-md-auto"
                prepend-icon="veticon-add-to-cart"
                :size="display.mdAndUp.value ? 'default' : 'small'"
                data-testid="add-to-cart-button"
                data-track-id="product_add_to_cart"
                @click="quantityValue = Math.max(1, props.orderCondition?.min ?? 0, props.orderCondition?.multiplier ?? 0)"
              >
                {{ $t('addToCart.button') }}
              </vmz-btn>
            </v-col>
            <v-col v-if="lastOrderedQuantity && lastOrderedQuantity > 0" cols="2" class="d-flex justify-end">
              <vmz-btn
                :disabled="props.humanMedicineRestricted || !hasCustomerReference(customerReference)"
                color="primary"
                class="font-weight-bold px-3 rounded-lg w-100"
                size="sm"
                data-testid="last-order-button"
                data-track-id="product_add_last_ordered"
                @click="quantityValue = lastOrderedQuantity"
              >
                +{{ lastOrderedQuantity }}
              </vmz-btn>
            </v-col>
          </v-row>
          <v-row v-else no-gutters>
            <v-col cols="12" class="d-flex justify-md-end ">
              <VmzQuantityChange
                v-model:quantity-value="quantityValue"
                :multiplier="props.orderCondition?.multiplier"
                :max-quantity="props.orderCondition?.max"
                :min-quantity="props.orderCondition?.min"
              />
            </v-col>
          </v-row>
          <p v-if="lastOrderedQuantity" class="text-caption text-vm-grey-7 ma-0 pa-0 position-relative text-right" style="line-height: 0;top: 11px;">
            {{ props.lastOrderedDate ? $t('lastOrderQuantity.captionAndDate', {
              date: $moment(props.lastOrderedDate).format($t('date.format')),
            }) : $t('lastOrderQuantity.caption') }} : {{ lastOrderedQuantity }}
          </p>
        </v-container>
      </v-col>
    </v-row>
  </v-container>
</template>

<style lang="scss" scoped>
.vmz-btn-add-to-cart {
  &:disabled {
    background-color: rgb(var(--v-theme-vm-grey-1)) !important;
    :deep(.v-btn__overlay) {
      opacity: 0 !important;
    }
    :deep(.v-btn__content), :deep(.v-btn__prepend) {
      color: rgb(var(--v-theme-vm-grey-3)) !important;
    }
  }
}
</style>
